import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, FormControl, TextField, InputLabel, Select, MenuItem, Backdrop, CircularProgress, Button, Autocomplete, Box } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Pagination } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ReportsHeader from './ReportsHeader';
import axios from '../../helpers/axios';
import newURL from '../../helpers/newUrl';
import { isUatEnvironment } from '../../helpers/uatMasking';

interface IProps {
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
    is_active: boolean;
    selectedHierarchies: any[];
    selectedHierarchyIds: any[];
  };
}

interface IState {
  balanceData: any[];
  currentPage: number;
  pageSize: number;
  totalRecords: number; 
  communityId: number;
  cityId: number;
  startDate: any;
  endDate: any;
  communities: any[];
  cities: any[];
  pageSizeOptions: number[]; 
}

const BalanceSheet = ({ user }: IProps) => {
  const [state, setState] = useState<IState>({
    balanceData: [],
    currentPage: 0,
    pageSize: 10,
    totalRecords: 0, 
    communityId: 0,
    cityId: 0,
    startDate: null,
    endDate: null,
    communities: [],
    cities: [],
    pageSizeOptions: [10,25,50,100], 
  });

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchCommunities();
  }, [state.cityId]);

  useEffect(() => {
    fetchBalanceData();
  }, [state.currentPage, state.pageSize]); 

  useEffect(() => {
    fetchBalanceData();
  }, [state.communityId, state.cityId, state.startDate, state.endDate]);

  const fetchCities = async () => {
    try {
      const response = await axios.get('accessibleHierarchiesList');
      if (response.data.success) {
        setState((prev) => ({ ...prev, cities: response.data.data }));
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchCommunities = async () => {
    try {
      let params = {};
      if (state.cityId) {
        params = { hierarchies: [state.cityId] };
      } else {
        params = { hierarchies: user.selectedHierarchyIds };
      }

      const response = await axios.get('allCommunitiesWithHierarchyLite', {
        params: params
      });
      if (response.data.success) {
        setState((prev) => ({ ...prev, communities: response.data.data }));
      }
    } catch (error) {
      console.error("Error fetching communities:", error);
    }
  };

  const fetchBalanceData = async () => {
    try {
      setShowLoader(true);
      const params = {
        page: state.currentPage + 1,
        limit: state.pageSize,
        community: state.communityId,
        city: state.cityId,
        startDate: state.startDate ? state.startDate.toISOString() : null,
        endDate: state.endDate ? state.endDate.toISOString() : null,
      };

      const response = await newURL.get('/BalanceSheet', { params });
      setState((prev) => ({
        ...prev,
        balanceData: response.data.data,
        totalRecords: response.data.totalRecords || 100, 
      }));
    } catch (error) {
      console.error("Error fetching balance data:", error);
    } finally {
      setShowLoader(false);
    }
  };

  const handlePaginationChange = (event: any, page: number) => {
    setState((prev) => ({ ...prev, currentPage: page - 1 }));
  };

  const handlePageSizeChange = (event: any) => {
    setState((prev) => ({ ...prev, pageSize: parseInt(event.target.value, 10), currentPage: 0 }));
  };

  const handleCommunityChange = (event: any,selectedOption: any) => {
    setState((prev) => ({ ...prev, communityId:  selectedOption ? selectedOption.id : 0, currentPage: 0 }));
  };

  const handleCityChange = (event: any) => {
    setState((prev) => ({ ...prev, cityId: event.target.value, communityId: 0, currentPage: 0 }));
  };

  const handleStartDateChange = (newValue: any) => {
    setState((prev) => ({ ...prev, startDate: newValue, currentPage: 0 }));
  };

  const handleEndDateChange = (newValue: any) => {
    setState((prev) => ({ ...prev, endDate: newValue, currentPage: 0 }));
  };

  const renderMaskedCell = (params: any) => (
    <Box
      component="span"
      sx={isUatEnvironment ? {
        filter: 'blur(4px)',
        opacity: 0.5,
        userSelect: 'none',
      } : undefined}
    >
      {params.value}
    </Box>
  );

  const columns = [
    {
      field: 'id',
      headerName: 'UID',
      width: 100,
      renderCell: (params: any) => (
        <a
          href={`/neighbourhood/customers/${params.value}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'black' }}
        >
          {params.value}
        </a>
      ),
    },
    { field: 'address', headerName: 'Address', width: 150, renderCell: renderMaskedCell },
    { field: 'community', headerName: 'Community', width: 150 },
    { field: 'city', headerName: 'City', width: 150 },
    { field: 'total_weight_kg', headerName: 'Total Weight (kg)', width: 150 },
    { field: 'total_price', headerName: 'Total Price', width: 105 },
    { field: 'total_withdrawals', headerName: 'Total Withdrawals', width: 135 },
    { field: 'current_balance', headerName: 'Current Balance', width: 200 },
  ];
  const handleExportToXLSX = async () => {
    try {
      setShowLoader(true); 
      const params = {
        community: state.communityId,
        city: state.cityId,
      };
  
      const response = await newURL.get('/BalanceSheet-export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'balance_sheet.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting to XLSX:", error);
    } finally {
      setShowLoader(false); 
    }
  };
  
  
  return (
    <div>
      <ReportsHeader user={user}/>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={showLoader}
      >
        <CircularProgress className='customLoader' />
      </Backdrop>
      <Card style={{ marginTop: '20px', padding: '20px', background: '#fff', boxShadow: '0px 4px 6px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Grid container spacing={2} style={{ marginBottom: '20px' }}>
            <Grid item md={1.5} xs={12}>
              <FormControl sx={{ width: '125px'}} size="small">
                <InputLabel>City</InputLabel>
                <Select
                  value={state.cityId}
                  onChange={handleCityChange}
                >
                  <MenuItem value="0" >All</MenuItem>
                  {state.cities.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item md={3} xs={12} >
              <FormControl sx={{ width: '150px'}} size="small">
                <InputLabel>Community</InputLabel>
                <Select
                  value={state.communityId}
                  onChange={handleCommunityChange}
                >
                  <MenuItem value="0">All</MenuItem>
                  {state.communities.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
             <Grid item md={2.5} xs={12} >
              <Autocomplete
                value={state.communities.find(c => c.id === state.communityId) || null}
                onChange={handleCommunityChange}
                options={state.communities}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                <TextField
                    {...params}
                    label="Community"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            {/* <Grid item md={3} xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={state.startDate}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item md={3} xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={state.endDate}
                  onChange={handleEndDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid> */}
            <Grid item md={8} xs={12} sx={{ display: 'flex', justifyContent: 'flex-end',p:'8px' }}>
              <Button variant="contained" size="small" sx={{ outline: "none !important" ,position: 'relative'}} onClick={handleExportToXLSX}>
              {showLoader && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        left: 10, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}
                    >
                      <CircularProgress size={20} className='customLoader' />
                    </Box>
                )}
                Export To XLSX
              </Button>
            </Grid>
          </Grid>
          <div style={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={state.balanceData}
              columns={columns}
              pageSize={state.pageSize}
              disableSelectionOnClick
              getRowId={(row) => row.id}
              hideFooter
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <FormControl sx={{ width: '100px', marginRight: '10px' }} size="small">
                    <InputLabel>Page Size</InputLabel>
                    <Select
                      value={state.pageSize}
                      onChange={handlePageSizeChange}
                    >
                      {state.pageSizeOptions.map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </Select>
            </FormControl>
            <Pagination
              count={Math.ceil(state.totalRecords / state.pageSize)} 
              page={state.currentPage + 1}
              onChange={handlePaginationChange}
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  border: '1px solid lightgray',
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  color: '#1976d2',
                  border: '1px solid rgba(25, 118, 210, 0.5)',
                },
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BalanceSheet;
